/**
 * DisconnectConfirmDialog - warns user before disconnecting when tabs are open
 *
 * Shows the list of affected tabs and asks for confirmation before disconnecting.
 * If no tabs are affected, the disconnect should proceed without showing this dialog.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { AlertTriangle, FileText } from 'lucide-react';
import { hasUnsavedStoragePreview } from '../../workspace/storage-explorer/preview/storagePreviewSessionStore';

interface AffectedTab {
  id: string;
  title: string;
}

interface DisconnectConfirmDialogProps {
  open: boolean;
  connectionName: string;
  affectedTabs: AffectedTab[];
  onConfirm: () => void;
  onCancel: () => void;
}

export function DisconnectConfirmDialog({
  open,
  connectionName,
  affectedTabs,
  onConfirm,
  onCancel,
}: DisconnectConfirmDialogProps) {
  const tabCount = affectedTabs.length;
  const tabWord = tabCount === 1 ? 'tab' : 'tabs';
  // A storage tab editing a file in its preview loses those edits with the tab: say so here.
  const unsaved = new Set(affectedTabs.filter((tab) => hasUnsavedStoragePreview(tab.id)).map((tab) => tab.id));

  return (
    <AlertDialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && onCancel()}>
      <AlertDialogContent data-testid="disconnect-confirm-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 text-warning">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <AlertDialogTitle>Disconnect from {connectionName}?</AlertDialogTitle>
          </div>
        </AlertDialogHeader>

        <AlertDialogDescription asChild>
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-md">
              <AlertTriangle className="h-5 w-5 text-warning mt-0.5 shrink-0" />
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                You have {tabCount} open {tabWord} for this connection.
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Disconnecting will close these tabs:
              </p>
              <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
                {affectedTabs.map((tab) => (
                  <li key={tab.id} className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{tab.title}</span>
                    {unsaved.has(tab.id) && <span className="shrink-0 text-xs font-medium text-amber-700 dark:text-amber-300" data-testid="disconnect-unsaved-preview">unsaved file edits</span>}
                  </li>
                ))}
              </ul>
              {unsaved.size > 0 && (
                <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">Unsaved edits in a file preview are lost when its tab closes.</p>
              )}
            </div>

          </div>
        </AlertDialogDescription>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="confirm-disconnect-button"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            Disconnect & Close Tabs
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
